import React, { useState } from 'react';
import { ComplianceObligation, ObligationPriority, ObligationType, FrequencyType } from './types';
import { TablePagination, useTablePagination } from '../../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../../shared/useTableSort';

interface Props {
  obligations: ComplianceObligation[];
  onSelectObligation: (obl: ComplianceObligation) => void;
  onCreateObligation: (data: Partial<ComplianceObligation>) => void;
}

export const ObligationMasterTable: React.FC<Props> = ({
  obligations,
  onSelectObligation,
  onCreateObligation,
}) => {
  const {
    sortedItems,
    sortField,
    sortDirection,
    requestSort,
  } = useTableSort(obligations);

  const oblPage = useTablePagination(sortedItems, 25);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    obligation_code: '',
    title: '',
    description: '',
    obligation_type: 'RECURRING_PERIODIC' as ObligationType,
    frequency_type: 'MONTHLY' as FrequencyType,
    priority: 'MEDIUM' as ObligationPriority,
    lead_department: 'Flight Operations',
    primary_owner_id: 'USR-FO-LEAD',
    primary_owner_name: '',
    control_reference: '',
    lead_time_days: 14,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.obligation_code || !formData.title || !formData.primary_owner_name) return;
    onCreateObligation(formData);
    setShowModal(false);
    setFormData({
      obligation_code: '',
      title: '',
      description: '',
      obligation_type: 'RECURRING_PERIODIC',
      frequency_type: 'MONTHLY',
      priority: 'MEDIUM',
      lead_department: 'Flight Operations',
      primary_owner_id: 'USR-FO-LEAD',
      primary_owner_name: '',
      control_reference: '',
      lead_time_days: 14,
    });
  };

  const getPriorityBadge = (prio: ObligationPriority) => {
    switch (prio) {
      case 'CRITICAL':
        return <span className="badge-pill badge-danger">Critical</span>;
      case 'HIGH':
        return <span className="badge-pill badge-warning">High</span>;
      case 'MEDIUM':
        return <span className="badge-pill badge-info">Medium</span>;
      default:
        return <span className="badge-pill badge-neutral">Low</span>;
    }
  };

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', gap: '12px' }}>
        <div>
          <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', margin: '0 0 2px 0' }}>Compliance Obligations Master Register</h3>
          <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
            Operational compliance responsibilities derived from aviation regulations, assigned owners, and schedules
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn-primary"
          style={{ width: 'auto', padding: '8px 16px', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
        >
          <span>+ New Obligation Definition</span>
        </button>
      </div>

      <div className="table-container" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
        <table className="data-table">
          <thead>
            <tr>
              <SortableTh field="obligation_code" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Code & Title
              </SortableTh>
              <SortableTh field="lead_department" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Department & Owner
              </SortableTh>
              <SortableTh field="priority" sortField={sortField} sortDirection={sortDirection} onSort={requestSort} align="center">
                Priority
              </SortableTh>
              <SortableTh field="frequency_type" sortField={sortField} sortDirection={sortDirection} onSort={requestSort} align="center">
                Frequency / Type
              </SortableTh>
              <SortableTh field="is_active" sortField={sortField} sortDirection={sortDirection} onSort={requestSort} align="center">
                Status
              </SortableTh>
              <SortableTh field="active_instances_count" sortField={sortField} sortDirection={sortDirection} onSort={requestSort} align="center">
                Active Cycles
              </SortableTh>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {obligations.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>
                  No compliance obligations found.
                </td>
              </tr>
            ) : (
              oblPage.paginatedItems.map((obl) => (
                <tr key={obl.id} style={{ cursor: 'pointer' }} onClick={() => onSelectObligation(obl)}>
                  <td style={{ maxWidth: '320px' }}>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{obl.title}</div>
                    <div style={{ fontSize: '11px', color: '#0284c7', fontFamily: 'monospace' }}>{obl.obligation_code}</div>
                    <div style={{ fontSize: '12px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: '2px' }}>{obl.description}</div>
                  </td>
                  <td>
                    <div style={{ fontSize: '12px', fontWeight: 500, color: '#1e293b' }}>{obl.lead_department}</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>{obl.primary_owner_name}</div>
                  </td>
                  <td style={{ textAlign: 'center' }}>{getPriorityBadge(obl.priority)}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span className="badge-pill badge-neutral">
                      {obl.frequency_type}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span
                      className={
                        obl.status === 'ACTIVE'
                          ? 'badge-pill badge-success'
                          : 'badge-pill badge-warning'
                      }
                    >
                      {obl.status}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '12px', fontWeight: 500 }}>
                      <span style={{ color: '#1e293b' }}>{obl.open_instances_count || 0} Open</span>
                      {obl.overdue_instances_count && obl.overdue_instances_count > 0 ? (
                        <span className="badge-pill badge-danger" style={{ marginLeft: '6px', fontSize: '10px' }}>
                          {obl.overdue_instances_count} Overdue!
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectObligation(obl);
                      }}
                      className="btn-secondary"
                      style={{ width: 'auto', padding: '5px 12px', fontSize: '12px' }}
                    >
                      Instances →
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <TablePagination
        currentPage={oblPage.currentPage}
        totalPages={oblPage.totalPages}
        pageSize={oblPage.pageSize}
        totalItems={oblPage.totalItems}
        onPageChange={oblPage.setPage}
        onPageSizeChange={oblPage.setPageSize}
        itemLabel="obligations"
      />

      {/* Modal */}
      {showModal && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: '540px' }}>
            <div className="modal-header">
              <h3>New Compliance Obligation Definition</h3>
              <button onClick={() => setShowModal(false)} className="close-btn">×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-field">
                    <label>Obligation Code</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. OBL-DISPATCH-EFB-01"
                      value={formData.obligation_code}
                      onChange={(e) => setFormData({ ...formData, obligation_code: e.target.value })}
                    />
                  </div>
                  <div className="form-field">
                    <label>Priority</label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                    >
                      <option value="CRITICAL">Critical</option>
                      <option value="HIGH">High</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="LOW">Low</option>
                    </select>
                  </div>
                </div>

                <div className="form-field">
                  <label>Obligation Title</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Monthly EFB Fuel Logs & Commander Discretion Analysis"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  />
                </div>

                <div className="form-field">
                  <label>Description & Scope</label>
                  <textarea
                    rows={2}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Operational requirements and fulfillment methods..."
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-field">
                    <label>Obligation Type</label>
                    <select
                      value={formData.obligation_type}
                      onChange={(e) => setFormData({ ...formData, obligation_type: e.target.value as any })}
                    >
                      <option value="RECURRING_PERIODIC">Recurring / Periodic</option>
                      <option value="EVENT_DRIVEN">Event-Driven</option>
                      <option value="CONTINUOUS_MONITORING">Continuous Monitoring</option>
                      <option value="ONE_TIME">One-Time</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Frequency</label>
                    <select
                      value={formData.frequency_type}
                      onChange={(e) => setFormData({ ...formData, frequency_type: e.target.value as any })}
                    >
                      <option value="MONTHLY">Monthly</option>
                      <option value="QUARTERLY">Quarterly</option>
                      <option value="SEMI_ANNUAL">Semi-Annual</option>
                      <option value="ANNUAL">Annual</option>
                      <option value="WEEKLY">Weekly</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-field">
                    <label>Responsible Department</label>
                    <input
                      type="text"
                      required
                      value={formData.lead_department}
                      onChange={(e) => setFormData({ ...formData, lead_department: e.target.value })}
                    />
                  </div>
                  <div className="form-field">
                    <label>Primary Owner Name</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Captain Burak Demir"
                      value={formData.primary_owner_name}
                      onChange={(e) => setFormData({ ...formData, primary_owner_name: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  Save Obligation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
